import React, { FC, useEffect, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import { darkModeBackgroud } from '../../../../utils/constants';
import { useDarkModeStore } from '../../../../store/useDarkModeStore';
import { useLanguageStore } from '../../../../store/useLanguageStore';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/material/Stack';
import useScreenWidth from '../../../../store/useScreenWidth';
import useGetOpenTicketsByMonth from '../../../../store/ReactQueries/dashboards/useGetOpenTicketsByMonth';
import useGetClosedTicketsByMonth from '../../../../store/ReactQueries/dashboards/useGetClosedTicketsByMonth';

const legendColor = {
    openTicket: '#696CFF',
    closeTicket: '#35CFEF'
}
export const cardStyle = {
    py: 0,
    px: 0,
    width: 'auto',
    display: 'flex',
    height: '364px',
    borderRadius: 'md',
    flexDirection: 'column',
    boxShadow: 'rgba(50, 71, 92, 0.1) 0px 2px 10px 0px',
};

export interface MonthOfYear {
    monthNumber: number,
    month: string,
    openTicketCount: number;
    closedTicketCount: number;
}

interface TicketsByMonthProp {
    height?: number;
    year: number;
}

const monthOfYear: MonthOfYear[] = [
    { monthNumber: 1, month: 'January', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 2, month: 'February', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 3, month: 'March', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 4, month: 'April', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 5, month: 'May', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 6, month: 'June', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 7, month: 'July', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 8, month: 'August', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 9, month: 'September', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 10, month: 'October', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 11, month: 'November', openTicketCount: 0, closedTicketCount: 0 },
    { monthNumber: 12, month: 'December', openTicketCount: 0, closedTicketCount: 0 },
];

const TicketsByMonth: FC<TicketsByMonthProp> = ({ height, year }) => {

    const isSmallScreen = useScreenWidth();

    const [monthData, setMonthData] = useState<MonthOfYear[]>(monthOfYear);

    const { darkMode } = useDarkModeStore();
    const { language } = useLanguageStore();
    const { data: openTicketsByMonth } = useGetOpenTicketsByMonth(year);
    const { data: closedTicketsByMonth } = useGetClosedTicketsByMonth(year);

    const upCardStyle = {
        ...cardStyle,
        background: darkMode ? darkModeBackgroud : 'white',
        height: height ? `${height}px` : '300px'
    };

    useEffect(() => {
        let resetMonthData = [...monthOfYear];
        let upOpenTicketsByMonth = openTicketsByMonth ?? [];
        let upCloseTicketsByMonth = closedTicketsByMonth ?? [];

        if (upOpenTicketsByMonth.length > 0 || upCloseTicketsByMonth.length > 0) {
            const updatedMonthData = resetMonthData.map(month => {
                const openTicketById = upOpenTicketsByMonth.find(item => item.monthNumber === month.monthNumber);
                const closeTicketById = upCloseTicketsByMonth.find(item => item.monthNumber === month.monthNumber);
                return { ...month, openTicketCount: openTicketById?.ticketCount ?? 0, closedTicketCount: closeTicketById?.ticketCount ?? 0 };
            });

            setMonthData(updatedMonthData);
        } else setMonthData(monthOfYear);

    }, [openTicketsByMonth, closedTicketsByMonth]);

    const { openTicket, closeTicket } = legendColor;

    return (
        <Sheet sx={upCardStyle}>
            <div className={`d-flex flex-row justify-content-between position-relative height-fill-available`}>
                <div className='p-4' style={{ width: '100%' }}>
                    <h5 style={{ color: darkMode ? "white" : 'rgba(50, 71, 92, 0.87)', opacity: .9 }}>{language['TicketsByMonth']}</h5>
                    <hr className={darkMode ? 'text-white opacity-100' : ''} />
                    <div className='gap-2 d-flex flex-row align-item-center justify-content-evenly'>
                        <div
                            className='d-flex flex-row align-items-center gap-2'
                            style={{ color: darkMode ? 'white' : 'black' }}
                        >
                            <div style={{ width: 20, height: 20, background: openTicket }} />
                            {language['OpenTicket']}
                        </div>
                        <div
                            className='d-flex flex-row align-items-center gap-2'
                            style={{ color: darkMode ? 'white' : 'black' }}
                        >
                            <div style={{ width: 20, height: 20, background: closeTicket }} />
                            {language['ClosedTicket']}
                        </div>
                    </div>
                    <Stack direction="column" spacing={1} sx={{ width: '100%', height: '80%' }}>
                        <LineChart
                            key={year}
                            height={250}
                            grid={{ horizontal: true }}
                            series={[
                                {
                                    data: monthData.map((item) => item.openTicketCount),
                                    label: `${language['OpenTicket']}:`,
                                    color: openTicket,
                                },
                                {
                                    data: monthData.map((item) => item.closedTicketCount),
                                    label: `${language['ClosedTicket']}:`,
                                    color: closeTicket,
                                },
                            ]}
                            margin={{
                                top: 20,
                                bottom: 20,
                            }}
                            xAxis={[
                                {
                                    scaleType: 'band',
                                    tickLabelStyle: { fill: darkMode ? 'white' : '' },
                                    data: monthData.map((item) => isSmallScreen ? language[item.month].slice(0, 3) : language[item.month]),
                                    valueFormatter: (value) => value,
                                },
                                {
                                    data: monthData.map(item => item.closedTicketCount),
                                    tickLabelStyle: { fill: darkMode ? 'white' : '' },
                                },
                            ]}
                            yAxis={[
                                {
                                    data: monthData.map(item => item.openTicketCount),
                                    tickLabelStyle: { fill: darkMode ? 'white' : '' },
                                },
                                {
                                    data: monthData.map(item => item.closedTicketCount),
                                    tickLabelStyle: { fill: darkMode ? 'white' : '' },
                                },
                            ]}
                            sx={{
                                '& .MuiChartsLegend-root': {
                                    display: 'none',
                                },
                            }}
                        />
                    </Stack>
                </div>
            </div>
        </Sheet>
    );
};

export default TicketsByMonth;
